import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader, XCircle } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const SubscriptionSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState('Processing your subscription...');
  const navigate = useNavigate();

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      setStatus('error');
      setMessage('No session ID found');
      return;
    }

    // Poll for subscription status
    pollSubscriptionStatus();
  }, [searchParams]);

  const pollSubscriptionStatus = async (attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      setStatus('error');
      setMessage('Unable to verify subscription. Please check your email or contact support.');
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const res = await axios.get(`${API}/api/subscriptions/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.active) {
        setStatus('success');
        const tierName = res.data.subscription.tier;
        setMessage(`Successfully subscribed to ${tierName.charAt(0).toUpperCase() + tierName.slice(1)}!`);
        
        // Redirect to home after 3 seconds
        setTimeout(() => navigate('/'), 3000);
        return;
      }

      // Continue polling
      setTimeout(() => pollSubscriptionStatus(attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error polling subscription:', error);
      setTimeout(() => pollSubscriptionStatus(attempts + 1), pollInterval);
    }
  };

  return (
    <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center pt-24 pb-32">
      <div className="max-w-md w-full mx-auto px-6 text-center">
        <div className="bg-[#1A1A1A] p-12 rounded-lg">
          {status === 'processing' && (
            <>
              <Loader className="w-16 h-16 text-[#D4AF37] animate-spin mx-auto mb-6" />
              <h1 className="font-['Bebas_Neue'] text-3xl mb-4">Processing...</h1>
              <p className="text-[#A3A3A3]">{message}</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
              <h1 className="font-['Bebas_Neue'] text-3xl mb-4 text-green-500">Success!</h1>
              <p className="text-[#A3A3A3] mb-6">{message}</p>
              <p className="text-sm text-[#737373]">Redirecting you to the homepage...</p>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
              <h1 className="font-['Bebas_Neue'] text-3xl mb-4 text-red-500">Error</h1>
              <p className="text-[#A3A3A3] mb-6">{message}</p>
              <button
                onClick={() => navigate('/subscription')}
                className="px-6 py-3 bg-[#D4AF37] text-black font-bold rounded hover:bg-[#C4A137] transition-colors"
              >
                Back to Subscription Page
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionSuccessPage;
